/**
 * TasksSection — lightweight CRUD task list scoped to a project.
 *
 * Phase 1 of the "Project as ecosystem of work" track (June 7, 2026).
 * Backed by ``/projects/{id}/tasks`` (list / create / patch /
 * soft-delete). Intentionally MUCH less ambitious than Jira / Notion
 * sub-tasks: every row is title + priority + due-date + checkbox +
 * delete. Anything richer (subtasks, dependencies, assignees) is out of
 * scope; the panel is meant to be the "post-it on the project" surface.
 *
 * UX rhythm:
 *   - Top inline input + ``Aggiungi`` button — same pattern the user
 *     already meets in the chat composer, so muscle memory transfers.
 *   - Each task row: checkbox (flip ``completed``), title, due chip,
 *     trailing ``×`` to soft-delete. The checkbox uses the dedicated
 *     ``completed`` PATCH field so the BE mark/unmark helpers fire
 *     (preserving ``completed_at`` semantics).
 *   - Empty state explains the panel's purpose so first-time users
 *     don't see a blank.
 *
 * Optimism: the mutations rely on TanStack's invalidation (no manual
 * optimistic update). The lists are tiny so the perceived delay is the
 * mutation roundtrip; adding optimistic updates would buy little and
 * require shadowing the task feed in the cache.
 */

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { CalendarClock, Loader2, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import type { ProjectTask } from "@ando/core/api";
import { Button, cn, toast } from "@ando/ui";

import {
  useCreateProjectTask,
  useDeleteProjectTask,
  useProjectTasks,
  useUpdateProjectTask,
} from "../../hooks/useProjects.js";
import { formatRelativeDateI18n } from "../../utils/relativeDate.js";

export interface TasksSectionProps {
  readonly projectId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isOverdue(task: ProjectTask): boolean {
  if (task.completed_at) return false;
  if (!task.due_at) return false;
  const t = Date.parse(task.due_at);
  if (Number.isNaN(t)) return false;
  return t < Date.now();
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const TaskRow: React.FC<{
  readonly task: ProjectTask;
  readonly projectId: string;
}> = ({ task, projectId }) => {
  const { i18n } = useLingui();
  const updateMutation = useUpdateProjectTask(projectId);
  const deleteMutation = useDeleteProjectTask(projectId);

  const completed = task.completed_at !== null;
  // Forward-facing labels (no "ieri" — overdue tasks read the date as
  // "DD/MM" rather than the awkward "ieri" — UX choice). Lingui-aware
  // so the anchor labels (``oggi`` / ``domani``) round-trip through the
  // extracted catalog.
  const dueLabel = task.due_at
    ? formatRelativeDateI18n(task.due_at, i18n, { recogniseYesterday: false })
    : "";
  const due = dueLabel.length > 0 ? dueLabel : null;
  const overdue = isOverdue(task);

  const handleToggle = React.useCallback(() => {
    updateMutation.mutate(
      { taskId: task.task_id, patch: { completed: !completed } },
      {
        onError: (err) => {
          toast.error(
            err instanceof Error
              ? i18n._(msg`Impossibile aggiornare l'attività: ${err.message}`)
              : i18n._(msg`Impossibile aggiornare l'attività`),
          );
        },
      },
    );
  }, [updateMutation, task.task_id, completed, i18n]);

  const handleDelete = React.useCallback(() => {
    deleteMutation.mutate(task.task_id, {
      onError: (err) => {
        toast.error(
          err instanceof Error
            ? i18n._(msg`Impossibile eliminare l'attività: ${err.message}`)
            : i18n._(msg`Impossibile eliminare l'attività`),
        );
      },
    });
  }, [deleteMutation, task.task_id, i18n]);

  return (
    <div
      className={cn(
        "group flex items-center gap-2.5 rounded-md px-2.5 py-2",
        "transition-colors hover:bg-ink-1/[0.025]",
      )}
    >
      <input
        type="checkbox"
        checked={completed}
        onChange={handleToggle}
        disabled={updateMutation.isPending}
        aria-label={
          completed
            ? i18n._(msg`Riapri attività: ${task.title}`)
            : i18n._(msg`Completa attività: ${task.title}`)
        }
        className={cn(
          "h-4 w-4 shrink-0 rounded border-hairline accent-ando-orange",
          "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50",
        )}
      />
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-[13px]",
          completed ? "text-ink-3 line-through" : "text-ink-1",
        )}
        title={task.title}
      >
        {task.title}
      </span>
      {due ? (
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 font-medium text-[10.5px]",
            overdue
              ? "bg-error/15 text-error"
              : completed
                ? "bg-ink-1/[0.05] text-ink-3"
                : "bg-ink-1/[0.05] text-ink-2",
          )}
          title={task.due_at ?? undefined}
        >
          <CalendarClock className="h-2.5 w-2.5" aria-hidden />
          {due}
        </span>
      ) : null}
      <button
        type="button"
        onClick={handleDelete}
        disabled={deleteMutation.isPending}
        aria-label={i18n._(msg`Elimina attività: ${task.title}`)}
        title={i18n._(msg`Elimina`)}
        className={cn(
          "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
          "text-ink-3 transition-opacity hover:bg-error/10 hover:text-error",
          "opacity-0 focus-visible:opacity-100 group-hover:opacity-100",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/35",
          deleteMutation.isPending && "opacity-60",
        )}
      >
        {deleteMutation.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : (
          <Trash2 className="h-3 w-3" aria-hidden />
        )}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export const TasksSection: React.FC<TasksSectionProps> = ({ projectId }) => {
  const { i18n } = useLingui();
  const query = useProjectTasks(projectId);
  const createMutation = useCreateProjectTask(projectId);

  const [draft, setDraft] = React.useState("");

  const handleAdd = React.useCallback(() => {
    const title = draft.trim();
    if (title.length === 0) return;
    createMutation.mutate(
      { title },
      {
        onSuccess: () => setDraft(""),
        onError: (err) => {
          toast.error(
            err instanceof Error
              ? i18n._(msg`Impossibile creare l'attività: ${err.message}`)
              : i18n._(msg`Impossibile creare l'attività`),
          );
        },
      },
    );
  }, [draft, createMutation, i18n]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleAdd();
      }
    },
    [handleAdd],
  );

  const tasks = query.data ?? [];
  const pending = tasks.filter((t) => t.completed_at === null);
  const done = tasks.filter((t) => t.completed_at !== null);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={i18n._(msg`Nuova attività…`)}
          aria-label={i18n._(msg`Titolo della nuova attività`)}
          className={cn(
            "h-9 flex-1 rounded-md border border-hairline bg-white/85 px-3",
            "text-[13px] text-ink-1 placeholder:text-ink-3",
            "focus-visible:border-ando-orange/45 focus-visible:outline-none",
            "focus-visible:ring-2 focus-visible:ring-ando-orange/25",
          )}
        />
        <Button
          variant="primary"
          size="sm"
          onClick={handleAdd}
          disabled={draft.trim().length === 0 || createMutation.isPending}
        >
          {createMutation.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          ) : (
            <Plus className="h-3.5 w-3.5" aria-hidden />
          )}
          <Trans>Aggiungi</Trans>
        </Button>
      </div>

      {query.isLoading ? (
        <div className="flex items-center gap-2 text-[12px] text-ink-3">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
          <Trans>Caricamento attività…</Trans>
        </div>
      ) : null}

      {!query.isLoading && tasks.length === 0 ? (
        <div className="rounded-md border border-hairline border-dashed bg-card/40 px-4 py-6 text-center text-[12.5px] text-ink-2 leading-relaxed">
          <Trans>
            Nessuna attività ancora. Annota qui le cose da fare relative al
            progetto: l'agente può aggiungerne dalle chat e tu puoi crearne di
            tue.
          </Trans>
        </div>
      ) : null}

      {pending.length > 0 ? (
        <div className="flex flex-col gap-1">
          {pending.map((t) => (
            <TaskRow key={t.task_id} task={t} projectId={projectId} />
          ))}
        </div>
      ) : null}

      {done.length > 0 ? (
        <details className="group">
          <summary
            className={cn(
              "cursor-pointer select-none rounded-md px-2.5 py-1.5",
              "font-medium text-[11px] text-ink-3 uppercase tracking-wider",
              "hover:bg-ink-1/[0.03] hover:text-ink-2",
            )}
          >
            <Trans>Completate</Trans>{" "}
            <span className="ml-1 tabular-nums">({done.length})</span>
          </summary>
          <div className="mt-1 flex flex-col gap-1">
            {done.map((t) => (
              <TaskRow key={t.task_id} task={t} projectId={projectId} />
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
};

TasksSection.displayName = "TasksSection";
